import { api, writeDB } from './store';
import { groupBy, uniq } from 'lodash';
import { Event, getEventSalesStatus } from './event';
import { SeatStatus, VenueSection } from './venue';
import { apollo } from './graphql';
import { BLOCK_SEATS, UNBLOCK_SEATS } from './queries/blockSeats';
import { CONTINGENT, SHIFT_CONTINGENT, CHANGE_CAPACITY, ALLOCATION } from './queries/event';
import { useSequentialPolling, SequentialPollingState } from './data';
import { Seat } from './seatmap/types';
import { compact } from 'redux-database/dist/util';

export const allSeatStatuses = ['available', 'blocked', 'cart', 'reserved', 'sold'] as const;
export type SeatStatusType = (typeof allSeatStatuses)[number];
export type EditableSeatStatusType = 'available' | 'blocked';

/* INTERFACES */
export interface AllocationSeat {
  blockReason?: string | null;
  number?: string;
  row?: string;
  id?: string;
  seatId?: string;
  status?: string;
}

export interface Allocation {
  section: {
    id: string;
    name: string;
  };
  capacity: number;
  available: number;
  systemSold: number;
  systemFree: number;
  processing: number;
  local: number;
  reserved: number;
  cart: number;
  blocked: number;
  blockedSeats: Array<{
    blockReason: string;
    seats: Array<{
      id: string;
      name: string;
      row: string;
    }>;
  }>;
}

export interface Contingent {
  sectionId: string;
  capacity: number;
  valid: number;
  reserved: number;
  cart: number;
  available: number;
  blocked: number;
  pools: Array<{ name: string; count: number }>;
}

export interface AllocatedSection extends VenueSection {
  allocation: Allocation;
}

export async function loadAllocations(
  event: { id: string },
  { updateGeneralAdmissionOnly = false }: { updateGeneralAdmissionOnly?: boolean } = {}
): Promise<Allocation[]> {
  const result = await apollo.query({
    query: ALLOCATION,
    variables: { id: event.id, generalAdmissionOnly: updateGeneralAdmissionOnly },
    fetchPolicy: 'network-only',
  });

  if (result.data.event?.allocation) {
    const responseAllocations: Allocation[] = result.data.event.allocation.map((row) => {
      const groups = groupBy(row.blockedSeats, 'blockReason');
      const blockedSeats: Allocation['blockedSeats'] = Object.keys(groups).map((blockReason) => ({
        blockReason,
        seats: groups[blockReason].map((item) => item.seat),
      }));
      return {
        ...row,
        blockedSeats: blockedSeats,
      };
    });
    if (updateGeneralAdmissionOnly) {
      const existingAllocations = writeDB.table('events').find(event.id)?.allocations || [];
      const mergedAllocations = new Map(existingAllocations.map((a) => [a.section.id, a]));
      responseAllocations.forEach((a) => mergedAllocations.set(a.section.id, a));
      writeDB.table('events').update(event, { allocations: Array.from(mergedAllocations.values()) });
    } else {
      writeDB.table('events').update(event, { allocations: responseAllocations });
    }
    return responseAllocations;
  }
  return [];
}

export async function loadContingent(event: Event, sectionId: string): Promise<Contingent | null> {
  const result = await apollo.query({
    query: CONTINGENT,
    variables: { id: event.id, sectionId },
    fetchPolicy: 'network-only',
  });

  const contingent: Contingent | undefined = result.data?.event?.contingent && {
    ...result.data.event.contingent,
    sectionId,
  };

  if (contingent) {
    const contingents = [...(event.contingents || []).filter((e) => e.sectionId !== contingent.sectionId), contingent];
    writeDB.table('events').update(event, { contingents });
  }
  return contingent ?? null;
}

export function parseSeatStatuses(eventSeatStatus: SeatStatus[]): { [id: string]: SeatStatus } {
  const seatStatuses: { [id: string]: SeatStatus } = {};
  eventSeatStatus.forEach((seat) => {
    const permanent = seat.blockReason?.startsWith('[PERM]_');
    const blockReason = seat.blockReason?.replace('[PERM]_', '');
    seatStatuses[seat.seatId] = { ...seat, blockReason, permanent };
  });
  return seatStatuses;
}

export async function loadSeatStatus(event: { id: string }): Promise<Record<string, SeatStatus>> {
  const response = await api.indexSeatStatuses({ eventId: event.id });
  const eventSeatStatus = response.data;

  if (eventSeatStatus) {
    const seatStatuses = {};
    eventSeatStatus.forEach((seat) => {
      const permanent = seat.blockReason?.startsWith('[PERM]_');
      const blockReason = seat.blockReason?.replace('[PERM]_', '');
      seatStatuses[seat.seatId] = { ...seat, blockReason, permanent };
    });
    writeDB.table('events').update(event, {
      seatStatus: seatStatuses,
    });
    return seatStatuses;
  } else {
    return {};
  }
}

export async function loadSeatWithId(
  itemId: string,
  eventId: string
): Promise<
  SeatStatus & {
    orderId?: string | undefined;
    orderReference?: string | undefined;
  }
> {
  return (await api.showSeatStatus({ eventId, itemId })).data as SeatStatus;
}

/* ALLOCATIONS UPDATE */
async function updateSeats({ seats, event }: { seats?: AllocationSeat[]; event: Event }): Promise<void> {
  const updatedSeats = {};
  seats?.forEach((seat) => {
    if (seat.seatId) {
      updatedSeats[seat.seatId] = {
        id: seat.id,
        status: seat.status,
        blockReason: seat.blockReason?.replace('[PERM]_', ''),
        permanent: seat.blockReason?.startsWith('[PERM]_'),
      };
    }
  });
  writeDB.table('events').update(event.id, {
    seatStatus: {
      ...event.seatStatus,
      ...updatedSeats,
    },
  });
  await getEventSalesStatus(event.id);
}

export async function blockSeats({
  eventId,
  itemsIds,
  reason,
  permanent,
  skipUpdate = false,
}: {
  eventId: string;
  itemsIds: string[];
  reason: string;
  permanent?: boolean;
  skipUpdate?: boolean;
}): Promise<void> {
  const variables = {
    eventId,
    itemsIds,
    blockReason: permanent ? `[PERM]_${reason}` : reason,
  };

  const resp = await apollo.mutate({
    mutation: BLOCK_SEATS,
    variables,
  });

  if (skipUpdate) return;

  const respSeats = resp.data?.blockSeats?.seats;

  const event = writeDB.table('events').find(eventId);
  if (event) {
    await updateSeats({ seats: respSeats, event });
  }

  const savedEvent = writeDB.table('events').find(eventId);
  if (savedEvent) {
    loadAllocations(savedEvent);
  }
}

export async function unblockSeats({
  eventId,
  itemsIds,
  skipUpdate = false,
}: {
  eventId: string;
  itemsIds: string[];
  skipUpdate?: boolean;
}): Promise<void> {
  const variables = {
    eventId,
    itemsIds,
  };

  const resp = await apollo.mutate({
    mutation: UNBLOCK_SEATS,
    variables,
  });

  if (skipUpdate) return;

  const respSeats = resp.data?.unblockSeats?.seats;

  const event = writeDB.table('events').find(eventId);

  if (event) {
    await updateSeats({ seats: respSeats, event });
  }

  const savedEvent = writeDB.table('events').find(eventId);
  if (savedEvent) {
    loadAllocations(savedEvent);
  }
}

export async function shiftContingent(variables: {
  eventId: string;
  sectionId: string;
  pool: string | null;
  blockReason: string | null;
  amount: number;
}): Promise<void> {
  await apollo.mutate({
    mutation: SHIFT_CONTINGENT,
    variables,
  });
}

export async function changeCapacity(variables: {
  eventId: string;
  sectionId: string;
  blockReason: string | null;
  amount: number;
}): Promise<void> {
  await apollo.mutate({
    mutation: CHANGE_CAPACITY,
    variables,
  });
}

export function allSeatsEditable(seats: Seat[]): boolean {
  return compact(uniq(seats.map((e) => e.allocation?.status))).every((e) => ['blocked', 'available'].includes(e));
}

export function useSeatStatusPolling(event?: Event): SequentialPollingState {
  return useSequentialPolling({
    load: async () => {
      if (event) {
        await loadSeatStatus(event);
      }
    },
    interval: 10,
  });
}
